import { PredictionAdmin } from '@gleamer/types';
import classNames from 'classnames';
import React, { FormEvent, useState } from 'react';
import { usePermissions } from '../../../../hooks/usePermissions';
import {
  usePredictionProductCommands,
  usePredictionProducts,
} from '../../../../hooks/usePredictionProducts';
import { PERMISSIONS } from '../../../../utils/permissions';
import { PageButton } from '../../../atoms/PageButton';
import { PageTitle } from '../../../atoms/PageTitle';
import { Card, Field, inputClassName } from '../../../atoms/Surface';
import { TableBodyTd, TableHeadTh } from '../../../atoms/Table';
import { formatDateTime } from '../imports/format';

type PredictionProduct = PredictionAdmin.PredictionProduct;

/** What a code may hold: what viewer-api makes of a name — lower-case letters, digits, single underscores. */
export const CODE_PATTERN = /^[a-z0-9]+(_[a-z0-9]+)*$/;

/** The code viewer-api makes of a name when none is given, shown as the code field's placeholder. */
export function codeOf(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function errorText(error: unknown): string {
  return (error as Error)?.message ?? String(error);
}

function NewProduct() {
  const { create } = usePredictionProductCommands();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const badCode = code !== '' && !CODE_PATTERN.test(code);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate(
      {
        name: name.trim(),
        code: code.trim() || undefined,
        description: description.trim() || undefined,
      },
      {
        onSuccess: () => {
          setName('');
          setCode('');
          setDescription('');
        },
      }
    );
  };
  return (
    <Card
      title="New product"
      description="The code prefixes its imports' BigQuery tables and cannot be changed later."
    >
      <form
        aria-label="New product"
        onSubmit={submit}
        className="flex flex-wrap items-start gap-4"
      >
        <Field label="Name">
          <input
            className={classNames(inputClassName, 'w-64')}
            value={name}
            onChange={e => setName(e.target.value)}
            required
            maxLength={200}
          />
        </Field>
        <Field
          label="Code"
          hint={
            badCode ? (
              <span
                role="alert"
                className="text-dl-danger"
              >
                Lower-case letters, digits and single underscores only.
              </span>
            ) : (
              'Made from the name when left empty.'
            )
          }
        >
          <input
            className={classNames(inputClassName, 'w-56 font-mono')}
            value={code}
            placeholder={codeOf(name)}
            onChange={e => setCode(e.target.value)}
            maxLength={64}
            aria-invalid={badCode}
          />
        </Field>
        <Field
          label="Description"
          className="min-w-[16rem] flex-1"
        >
          <input
            className={inputClassName}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </Field>
        <div className="flex items-center gap-2 pt-6">
          <PageButton
            tone="primary"
            type="submit"
            disabled={!name.trim() || badCode || create.isLoading}
          >
            {create.isLoading ? 'Creating…' : 'Create'}
          </PageButton>
          {create.isError && (
            <span
              role="alert"
              className="text-sm text-dl-danger"
            >
              {errorText(create.error)}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function ProductRow({ product, canManage }: { product: PredictionProduct; canManage: boolean }) {
  const { update, remove } = usePredictionProductCommands();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description ?? '');
  const save = () =>
    update.mutate(
      { id: product.id, change: { name: name.trim(), description: description.trim() || null } },
      { onSuccess: () => setEditing(false) }
    );
  const error = update.error ?? remove.error;
  return (
    <tr
      className="group align-top"
      aria-label={`Product ${product.code}`}
    >
      <TableBodyTd className="pl-6">
        {editing ? (
          <input
            aria-label="Name"
            className={classNames(inputClassName, 'w-56')}
            value={name}
            onChange={e => setName(e.target.value)}
            maxLength={200}
          />
        ) : (
          <span className="font-medium">{product.name}</span>
        )}
      </TableBodyTd>
      <TableBodyTd className="font-mono text-sm text-dl-muted">{product.code}</TableBodyTd>
      <TableBodyTd className="max-w-md break-words text-dl-muted">
        {editing ? (
          <input
            aria-label="Description"
            className={classNames(inputClassName, 'w-full')}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        ) : (
          (product.description ?? '—')
        )}
      </TableBodyTd>
      <TableBodyTd className="text-right tabular-nums">
        {product.imports.toLocaleString()}
      </TableBodyTd>
      <TableBodyTd className="whitespace-nowrap">
        {product.holdsPredictions ? 'Yes' : 'None'}
      </TableBodyTd>
      <TableBodyTd className="whitespace-nowrap text-dl-muted">
        {formatDateTime(product.createdAt)}
      </TableBodyTd>
      {canManage && (
        <TableBodyTd className="pr-6">
          <div className="flex flex-wrap items-center justify-end gap-1">
            {editing ? (
              <>
                <PageButton
                  tone="primary"
                  onClick={save}
                  disabled={!name.trim() || update.isLoading}
                >
                  Save
                </PageButton>
                <PageButton
                  onClick={() => {
                    setEditing(false);
                    setName(product.name);
                    setDescription(product.description ?? '');
                    update.reset();
                  }}
                >
                  Cancel
                </PageButton>
              </>
            ) : deleting ? (
              <>
                <span className="text-sm">
                  Delete {product.name}
                  {product.imports > 0 && ` and its ${product.imports} empty imports`}?
                </span>
                <PageButton
                  tone="stop"
                  onClick={() => remove.mutate(product.id, { onSettled: () => setDeleting(false) })}
                  disabled={remove.isLoading}
                >
                  Confirm delete
                </PageButton>
                <PageButton onClick={() => setDeleting(false)}>Cancel</PageButton>
              </>
            ) : (
              <>
                <PageButton onClick={() => setEditing(true)}>Rename</PageButton>
                <PageButton
                  tone="stop"
                  onClick={() => {
                    remove.reset();
                    setDeleting(true);
                  }}
                  disabled={product.holdsPredictions}
                  title={
                    product.holdsPredictions
                      ? 'It holds predictions: delete its imports, or move them to another product, first.'
                      : undefined
                  }
                >
                  Delete
                </PageButton>
              </>
            )}
          </div>
          {error && (
            <p
              role="alert"
              className="mt-1 text-right text-sm text-dl-danger"
            >
              {errorText(error)}
            </p>
          )}
        </TableBodyTd>
      )}
    </tr>
  );
}

/**
 * The backoffice's Products page (read:prediction): the products prediction imports are bound to. A data
 * manager (create:prediction) creates them, renames and describes them, and deletes one no prediction belongs
 * to — with its empty imports.
 */
export function ProductsPage() {
  const { has } = usePermissions();
  const canManage = has(PERMISSIONS.CREATE_PREDICTION);
  const products = usePredictionProducts();
  return (
    <div className="flex flex-col gap-6">
      <PageTitle title="Products" />
      {canManage && <NewProduct />}
      <Card title="Products">
        {products.isLoading && <p className="text-dl-muted">Loading the products…</p>}
        {products.isError && (
          <p
            role="alert"
            className="text-dl-danger"
          >
            Could not load the products: {errorText(products.error)}
          </p>
        )}
        {products.isSuccess && products.data.length === 0 && (
          <p className="text-dl-muted">No product yet.</p>
        )}
        {products.isSuccess && products.data.length > 0 && (
          <div className="-mx-6 overflow-x-auto">
            <table
              className="w-full table-auto"
              aria-label="Products"
            >
              <thead>
                <tr>
                  <TableHeadTh className="pl-6">Name</TableHeadTh>
                  <TableHeadTh>Code</TableHeadTh>
                  <TableHeadTh>Description</TableHeadTh>
                  <TableHeadTh className="text-right">Imports</TableHeadTh>
                  <TableHeadTh>Predictions</TableHeadTh>
                  <TableHeadTh>Created</TableHeadTh>
                  {canManage && <TableHeadTh className="pr-6" />}
                </tr>
              </thead>
              <tbody>
                {products.data.map(product => (
                  <ProductRow
                    key={product.id}
                    product={product}
                    canManage={canManage}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export default ProductsPage;
